/**
 * Танк: the one machine on «Сучасна війна».
 *
 * Built on exactly the same idea as the човен, and for the same reason: **a
 * tank is not a body.** It carries one, and the one it carries already has a
 * collider, a camera and a set of controls. A tank with physics of its own
 * would be two things standing in one place arguing about where that place
 * is. So while somebody is in it, the tank *is* where they are - the driver
 * walks, at a tank's speed and in a tank's turning circle, and the hull is
 * drawn around them.
 *
 * What is left over is what this file is: how fast it goes, which way it is
 * pointing, how many shells it has taken, and what happens to the person
 * inside when it takes the fifth.
 *
 * "Танк можна розбити тільки пострілами з іншого танку за 5 попадань. Потім
 * танк вибухає, а гравець випадає з нього і втрачає 50% здоров'я."
 *
 * Pure: no world, no renderer, no input device.
 */

/** Blocks a second on the flat. Half again as fast as a person on foot. */
export const TANK_SPEED = 6.6;

/** And how fast it comes round, in radians a second. A tank is not a boat. */
export const TANK_TURN = 1.4;

/**
 * How quickly it gathers way and loses it.
 *
 * Heavier than a човен, which is at 1.6 - a tank leans back on its tracks
 * and takes a moment - but not so heavy that it is slower than walking for
 * the first four seconds, which is what 1.0 turned out to mean: two and a
 * half blocks in the opening second against a person's four and a third.
 */
export const TANK_DRAG = 2.2;

/** Where the driver's feet sit above the ground the tracks are on. */
export const TANK_FLOOR = 0.9;

/** How many shells it takes. "За 5 попадань." */
export const TANK_HITS = 5;

/** What the crew loses when it goes up: half of everything they have. */
export const TANK_BLAST_SHARE = 0.5;

/** How far the blast reaches, for anybody standing near it. */
export const TANK_BLAST_REACH = 4;

/** How long the barrel takes between shots, in seconds. */
export const TANK_RELOAD = 2.5;

/** How fast a снаряд leaves the barrel, in blocks a second. */
export const SHELL_SPEED = 42;

/** And how hard it falls, which is what makes aiming a thing you learn. */
export const SHELL_GRAVITY = 9;

/** How wide and tall a tank is, for a shell deciding whether it hit one. */
export const TANK_WIDTH = 2.4;
export const TANK_HEIGHT = 2.2;

export interface Tank {
  readonly id: number;
  x: number;
  y: number;
  z: number;
  /** Which way the hull points. */
  yaw: number;
  /** Speed along that heading. Negative is reversing. */
  way: number;
  /** Who is driving, or null. A client id, like a boat's rider. */
  rider: number | null;
  /** Shells taken. At `TANK_HITS` it is gone. */
  hits: number;
  /** Seconds until the gun will fire again. */
  reload: number;
}

let nextId = 1;

export function makeTank(
  at: { x: number; y: number; z: number }, yaw = 0,
): Tank {
  return {
    id: nextId++, x: at.x, y: at.y, z: at.z, yaw,
    way: 0, rider: null, hits: 0, reload: 0,
  };
}

/**
 * One the server owns, drawn here.
 *
 * Built rather than made-and-renumbered, exactly as a mirrored boat is: the
 * maker takes the next local id as it goes, so mirroring somebody else's
 * tank would push this page's own numbering along for a hull it never made.
 */
export function mirrorTank(
  id: number, at: { x: number; y: number; z: number }, yaw: number,
  hits = 0,
): Tank {
  return {
    id, x: at.x, y: at.y, z: at.z, yaw, way: 0, rider: null, hits, reload: 0,
  };
}

/** Which way the driver is pushing. The same shape the oars have. */
export interface Levers {
  readonly forward: boolean;
  readonly back: boolean;
  readonly left: boolean;
  readonly right: boolean;
}

export const IDLE: Levers = {
  forward: false, back: false, left: false, right: false,
};

/**
 * Where a tank may stand.
 *
 * Ground under it and air where the hull is. Not a collider: the driver's
 * own body is the collider, and this is only asked about the cells the hull
 * would sweep through, so a tank cannot be driven into a wall or off into
 * the air - it stops against them the way a boat stops against a bank.
 */
export interface Ground {
  /** The top of the ground in this column, or null for nowhere to stand. */
  floorAt(x: number, z: number): number | null;
}

/**
 * Drive for `dt` seconds.
 *
 * The hull turns whether or not it is moving - a tracked vehicle turns on
 * the spot, which is the one thing everybody knows about tanks - and it
 * climbs a block but not two: the ground it is going to has to be within a
 * block of the ground it is on, or it stops.
 *
 * @returns whether it actually moved, so the caller can make a noise.
 */
export function driveTank(
  tank: Tank, levers: Levers, dt: number, ground: Ground,
): boolean {
  if (levers.left) tank.yaw += TANK_TURN * dt;
  if (levers.right) tank.yaw -= TANK_TURN * dt;
  tank.reload = Math.max(0, tank.reload - dt);

  const want = (levers.forward ? 1 : 0) - (levers.back ? 0.5 : 0);
  if (want !== 0) {
    tank.way += (want * TANK_SPEED - tank.way) * Math.min(1, TANK_DRAG * dt);
  } else {
    tank.way -= tank.way * Math.min(1, TANK_DRAG * dt);
    if (Math.abs(tank.way) < 0.02) tank.way = 0;
  }
  if (tank.way === 0) {
    const here = ground.floorAt(tank.x, tank.z);
    if (here !== null) tank.y = here;
    return false;
  }

  const nx = tank.x + Math.sin(tank.yaw) * tank.way * dt;
  const nz = tank.z + Math.cos(tank.yaw) * tank.way * dt;
  const ahead = ground.floorAt(nx, nz);
  const here = ground.floorAt(tank.x, tank.z);
  if (ahead === null || (here !== null && Math.abs(ahead - here) > 1.01)) {
    // A wall, a cliff or a hole. Stop dead rather than grinding into it.
    tank.way = 0;
    if (here !== null) tank.y = here;
    return false;
  }
  tank.x = nx;
  tank.z = nz;
  tank.y = ahead;
  return true;
}

/** Did a shell at this point hit this tank? */
export function shellHits(
  tank: Tank, at: { x: number; y: number; z: number },
): boolean {
  const half = TANK_WIDTH / 2;
  return Math.abs(at.x - tank.x) <= half &&
    Math.abs(at.z - tank.z) <= half &&
    at.y >= tank.y - 0.2 && at.y <= tank.y + TANK_HEIGHT;
}

/**
 * A shell landed on it.
 *
 * @returns whether that was the fifth, and the tank is gone.
 */
export function shellStruck(tank: Tank): boolean {
  tank.hits++;
  return tank.hits >= TANK_HITS;
}

/**
 * What the blast takes off somebody standing this far from it.
 *
 * Half of their whole сила at the middle, tapering to nothing at the edge of
 * the reach - so the driver, who is *in* it, always loses the full half, and
 * whoever was walking past loses some of it. Given as a share rather than as
 * points because a page's own сила is a page's own business, which is the
 * arrangement everything else about health already has.
 */
export function blastShare(away: number): number {
  if (away >= TANK_BLAST_REACH) return 0;
  return TANK_BLAST_SHARE * (1 - away / TANK_BLAST_REACH);
}
